import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ZohoConfig, ZohoRegion } from '@/api/types';
import { ZOHO_REGIONS, REDACTION_SENTINEL } from '@/api/types';
import { Field, FormShell, SecretInput } from './shared';

interface Props {
  initial: ZohoConfig | null;
  onSubmit: (config: Partial<ZohoConfig>) => void;
  submitting: boolean;
  onCancel: () => void;
  onReset?: () => void;
}

export function ZohoForm({ initial, onSubmit, submitting, onCancel, onReset }: Props): JSX.Element {
  const { t } = useTranslation();
  const [region, setRegion] = React.useState<ZohoRegion>(initial?.region ?? 'eu');
  const [orgId, setOrgId] = React.useState(initial?.orgId ?? '');
  const [departmentId, setDepartmentId] = React.useState(initial?.departmentId ?? '');
  const [clientId, setClientId] = React.useState(initial?.clientId ?? '');
  const [clientSecret, setClientSecret] = React.useState(initial?.clientSecret ?? '');
  const [refreshToken, setRefreshToken] = React.useState(initial?.refreshToken ?? '');

  const preserveOr = (v: string): string =>
    v === REDACTION_SENTINEL || !v ? REDACTION_SENTINEL : v;

  return (
    <form
      id="integration-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          region,
          orgId: orgId.trim(),
          departmentId: departmentId.trim(),
          clientId: clientId.trim(),
          clientSecret: preserveOr(clientSecret),
          refreshToken: preserveOr(refreshToken),
        });
      }}
    >
      <FormShell submitting={submitting} onCancel={onCancel} onReset={onReset}>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('integrations.form.zoho.region')} htmlFor="zh-region" required>
            <Select value={region} onValueChange={(v) => setRegion(v as ZohoRegion)}>
              <SelectTrigger id="zh-region">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ZOHO_REGIONS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('integrations.form.zoho.orgId')} htmlFor="zh-org" required>
            <Input id="zh-org" value={orgId} onChange={(e) => setOrgId(e.target.value)} required />
          </Field>
        </div>
        <Field label={t('integrations.form.zoho.departmentId')} htmlFor="zh-dept" required>
          <Input
            id="zh-dept"
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            required
          />
        </Field>
        <Field label={t('integrations.form.zoho.clientId')} htmlFor="zh-cid" required>
          <Input id="zh-cid" value={clientId} onChange={(e) => setClientId(e.target.value)} required />
        </Field>
        <Field label={t('integrations.form.zoho.clientSecret')} htmlFor="zh-cs" required hint={t('integrations.form.preserveHint')}>
          <SecretInput id="zh-cs" value={clientSecret} onChange={setClientSecret} />
        </Field>
        <Field label={t('integrations.form.zoho.refreshToken')} htmlFor="zh-rt" required hint={t('integrations.form.preserveHint')}>
          <SecretInput id="zh-rt" value={refreshToken} onChange={setRefreshToken} />
        </Field>
      </FormShell>
    </form>
  );
}
